import { type FC, useState, useReducer, useEffect, useRef, useMemo } from "react";
import {
  type selectedItemProps,
  type ActionProps,
  formStateReducer,
  initialState,
} from "./modules";
import { Presentation, type ItemListProps, type ItemProps } from "./Presentation";

type Props = {
  categoryName: string;
  formName: string;
  itemsArrayWithParents: Record<
    string,
    {
      hasRecommendedLabel?: boolean;
      items: ItemProps[];
    }
  >;
  isBiyo?: boolean;
};

export type formStateContextProps = {
  formState: selectedItemProps;
  dispatchFormState: React.Dispatch<ActionProps>;
};

export const NestedDataMultiSelectModal: FC<Props> = ({
  categoryName,
  formName,
  itemsArrayWithParents,
  isBiyo,
}) => {
  // モーダル開閉
  const [isShow, setIsShow] = useState(false);
  // 選択した条件を管理するstate
  const [formState, dispatchFormState] = useReducer(formStateReducer, initialState);
  // モーダル内のチェック状態を保持
  const [selectedItems, setSelectedItems] = useState<selectedItemProps>({});
  // モーダル内アコーディオンの開閉状態を保持
  const [isAccordionOpen, setIsAccordionOpen] = useState<Record<number, boolean>>({});
  // 経験職種用のref
  const experienceInputRef = useRef<HTMLInputElement>(null);
  // 未経験職種用のref
  const inexperienceInputRef = useRef<HTMLInputElement>(null);

  const { experienceIds, inexperienceIds } = useMemo(() => {
    const experienceIdsArray: string[] = [];
    const inexperienceIdsArray: string[] = [];

    Object.values(formState).forEach((parent) => {
      Object.keys(parent).forEach((value) => {
        // 未経験判定フラグで判定
        if (parent[value]?.isInexperience) {
          inexperienceIdsArray.push(value);
        } else {
          experienceIdsArray.push(value);
        }
      });
    });

    return { experienceIds: experienceIdsArray, inexperienceIds: inexperienceIdsArray };
  }, [formState]);

  // 描画時のチェック状態をセット
  useEffect(() => {
    Object.entries(itemsArrayWithParents).forEach(([parentKey, parentData]) => {
      parentData.items
        .filter((item) => item.initialChecked)
        .forEach((item) => {
          dispatchFormState({
            type: "selectValue",
            parentKey,
            value: item.value,
            name: item.text,
            isInexperience: item.isInexperience,
          });
        });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addItem = ({
    parentKey,
    value,
    name,
    isInexperience,
  }: {
    parentKey: string;
    value: string;
    name: string;
    isInexperience?: boolean;
  }) => {
    setSelectedItems((prevItems) => ({
      ...prevItems,
      [parentKey]: {
        ...prevItems[parentKey],
        [value]: {
          name,
          isInexperience,
        },
      },
    }));
  };

  const removeItem = (parentKey: string, value: string) => {
    setSelectedItems((prevItems) => {
      const updatedParentKey = { ...prevItems[parentKey] };
      delete updatedParentKey[value];
      return {
        ...prevItems,
        [parentKey]: updatedParentKey,
      };
    });
  };

  const itemList: ItemListProps = Object.entries(itemsArrayWithParents).reduce(
    (result, [parentKey, parentData]) => {
      return {
        ...result,
        [parentKey]: {
          hasRecommendedLabel: parentData.hasRecommendedLabel,
          items: parentData.items.map((item) => ({
            ...item,
            onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
              // チェックボックスの状態が変更された時の処理
              if (event.target.checked) {
                addItem({
                  parentKey,
                  value: event.target.value,
                  name: event.target.name,
                  isInexperience: item.isInexperience,
                });
              } else {
                removeItem(parentKey, event.target.value);
              }
            },
            name: item.text,
            defaultChecked: !!(formState[parentKey] && formState[parentKey][item.value]),
          })),
        },
      };
    },
    {},
  );

  // モーダル内の条件追加ボタン
  const handleOnClick = () => {
    const getWillAddedItems = () => {
      const parentKeys = Object.keys(selectedItems);
      const willAddedItems = parentKeys.flatMap((parentKey) =>
        Object.keys(selectedItems[parentKey])
          .filter((value) => !formState[parentKey] || !formState[parentKey][value])
          .map((value) => ({
            parentKey,
            value,
            name: selectedItems[parentKey][value].name,
            isInexperience: selectedItems[parentKey][value].isInexperience,
          })),
      );
      return willAddedItems;
    };

    const getWillDeletedItems = () => {
      const parentKeys = Object.keys(formState);
      const willDeletedItems = parentKeys.flatMap((parentKey) =>
        Object.keys(formState[parentKey])
          .filter((value) => !selectedItems[parentKey] || !selectedItems[parentKey][value])
          .map((value) => ({
            parentKey,
            value,
            name: formState[parentKey][value].name,
          })),
      );
      return willDeletedItems;
    };

    const willAddedItems = getWillAddedItems();
    const willDeletedItems = getWillDeletedItems();

    willAddedItems.forEach((addedItem) => {
      dispatchFormState({
        type: "selectValue",
        parentKey: addedItem.parentKey,
        value: addedItem.value,
        name: addedItem.name,
        isInexperience: addedItem.isInexperience,
      });
    });

    willDeletedItems.forEach((addedItem) => {
      dispatchFormState({
        type: "unSelectValue",
        parentKey: addedItem.parentKey,
        value: addedItem.value,
      });
    });

    setIsShow(false);
  };

  useEffect(() => {
    setSelectedItems(formState);
  }, [formState]);

  // 経験職種IDが変更された時に検索結果を更新
  useEffect(() => {
    if (experienceInputRef.current) {
      experienceInputRef.current.value =
        experienceIds.length > 0 ? JSON.stringify(experienceIds) : "";
      const changeEvent = new Event("change", { bubbles: true });
      experienceInputRef.current.dispatchEvent(changeEvent);
    }

    if (inexperienceInputRef.current) {
      inexperienceInputRef.current.value =
        inexperienceIds.length > 0 ? JSON.stringify(inexperienceIds) : "";
      const changeEvent = new Event("change", { bubbles: true });
      inexperienceInputRef.current.dispatchEvent(changeEvent);
    }
  }, [experienceIds, inexperienceIds]);

  return (
    <Presentation
      formState={formState}
      dispatchFormState={dispatchFormState}
      categoryName={categoryName}
      formName={formName}
      setIsShow={setIsShow}
      isShow={isShow}
      itemList={itemList}
      handleOnClick={handleOnClick}
      setIsAccordionOpen={setIsAccordionOpen}
      isAccordionOpen={isAccordionOpen}
      inexperienceJobTypeIds={inexperienceIds}
      experienceJobTypeIds={experienceIds}
      experienceInputRef={experienceInputRef}
      inexperienceInputRef={inexperienceInputRef}
      isBiyo={isBiyo}
    />
  );
};
